/**
 * FleetTab — Company / fleet-level analysis view.
 *
 * Aggregates data across the current driver (ready for multi-driver
 * when the database layer supports it).
 *
 * Shows:
 *  - Key metrics summary table
 *  - Day-of-week utilisation bar chart
 *  - Vehicle utilisation table
 *  - Driver summary table (future multi-driver)
 */

import { useMemo } from 'react'
import { DriverAnalysis } from '../types'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts'

interface Props {
  analysis: DriverAnalysis
}

export function FleetTab({ analysis }: Props) {
  const { summary, daily_breakdown, infringements, vehicles_used } = analysis
  const driver = analysis.driver

  // Vehicle utilisation
  const vehicleStats = useMemo(() => {
    const map = new Map<string, { days: number; km: number; vin?: string; isCompany: boolean }>()
    for (const v of vehicles_used) {
      const key = v.registration_number
      const existing = map.get(key) || { days: 0, km: 0, vin: v.vin, isCompany: !!(v as any).is_company_vehicle }
      existing.days++
      existing.km += (v.odometer_end ?? 0) - (v.odometer_start ?? 0)
      map.set(key, existing)
    }
    return Array.from(map.entries()).map(([reg, s]) => ({
      reg,
      days: s.days,
      km: s.km,
      vin: s.vin,
      isCompany: s.isCompany,
    })).sort((a, b) => b.km - a.km)
  }, [vehicles_used])

  // Day-of-week analysis
  const dowData = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    const totals: Record<string, { driving: number; work: number; count: number }> = {}
    for (const d of days) totals[d] = { driving: 0, work: 0, count: 0 }

    for (const day of daily_breakdown) {
      const d = new Date(day.date)
      const dow = days[(d.getDay() + 6) % 7]
      totals[dow].driving += day.driving_minutes
      totals[dow].work += day.work_minutes
      totals[dow].count++
    }

    return days.map((d) => ({
      day: d,
      driving: +(totals[d].driving / 60).toFixed(1),
      work: +(totals[d].work / 60).toFixed(1),
      count: totals[d].count,
    }))
  }, [daily_breakdown])

  // Compliance score
  const complianceScore = useMemo(() => {
    if (daily_breakdown.length === 0) return 100
    const infringementDays = daily_breakdown.filter((d) => d.status === 'infringement').length
    return Math.round(((daily_breakdown.length - infringementDays) / daily_breakdown.length) * 100)
  }, [daily_breakdown])

  return (
    <div className="space-y-5">
      {/* Key Metrics */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Key Metrics</h3>
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-gray-500 w-48">Total Driving</td>
                <td className="py-2 px-3 font-medium text-gray-900">{summary.total_driving_hours}h</td>
                <td className="py-2 px-3 text-gray-500 w-48">Days Worked</td>
                <td className="py-2 px-3 font-medium text-gray-900">{summary.days_worked}</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-gray-500">Total Distance</td>
                <td className="py-2 px-3 font-medium text-gray-900">{summary.total_distance_km.toLocaleString()} km</td>
                <td className="py-2 px-3 text-gray-500">Avg Daily Driving</td>
                <td className="py-2 px-3 font-medium text-gray-900">{summary.avg_daily_driving_hours.toFixed(1)}h</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-gray-500">Other Work</td>
                <td className="py-2 px-3 font-medium text-gray-900">{summary.total_work_hours.toFixed(1)}h</td>
                <td className="py-2 px-3 text-gray-500">Avg Daily Distance</td>
                <td className="py-2 px-3 font-medium text-gray-900">{summary.avg_daily_distance_km} km</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-gray-500">Infringements</td>
                <td className="py-2 px-3">
                  <span className={`font-medium ${infringements.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {infringements.length}
                  </span>
                  {infringements.length > 0 && (
                    <span className="text-xs text-gray-400 ml-2">
                      ({infringements.filter(i => i.severity === 'very_serious').length} very serious,{' '}
                      {infringements.filter(i => i.severity === 'serious').length} serious,{' '}
                      {infringements.filter(i => i.severity === 'minor').length} minor)
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-gray-500">Compliance Score</td>
                <td className="py-2 px-3">
                  <span className={`font-medium ${complianceScore >= 90 ? 'text-green-600' : complianceScore >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                    {complianceScore}%
                  </span>
                </td>
              </tr>
              <tr>
                <td className="py-2 px-3 text-gray-500">Vehicles Used</td>
                <td className="py-2 px-3 font-medium text-gray-900">
                  {vehicleStats.length}
                  <span className="text-xs text-gray-400 ml-2">{vehicleStats.map(v => v.reg).join(', ')}</span>
                </td>
                <td className="py-2 px-3 text-gray-500">Period</td>
                <td className="py-2 px-3 font-medium text-gray-900">{daily_breakdown.length} days</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Day-of-week chart */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Hours by Day of Week</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={dowData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb' }}
              formatter={(v: number, name: string) => [`${v}h`, name]}
            />
            <Bar dataKey="driving" name="Driving" fill="#1d4ed8" />
            <Bar dataKey="work" name="Other Work" fill="#d97706" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Vehicle Utilisation */}
      {vehicleStats.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Vehicle Utilisation</h3>
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Vehicle</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Type</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">VIN</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Sessions</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Distance</th>
                </tr>
              </thead>
              <tbody>
                {vehicleStats.map((v) => (
                  <tr key={v.reg} className="border-b border-gray-100">
                    <td className="py-2 px-3 font-mono font-bold text-gray-900">{v.reg}</td>
                    <td className="py-2 px-3">
                      {v.isCompany ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded bg-blue-100 text-blue-700 border border-blue-200">Company</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded bg-gray-100 text-gray-600 border border-gray-200">Other</span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs text-gray-400">{v.vin || '—'}</td>
                    <td className="py-2 px-3 text-right text-gray-500">{v.days}</td>
                    <td className="py-2 px-3 text-right font-medium text-gray-900">{v.km.toLocaleString()} km</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Driver Summary (future multi-driver) */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Driver Summary</h3>
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Driver</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Card</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Driving</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Distance</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Days</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Infringements</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">Compliance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 font-medium text-gray-900">{driver?.first_names} {driver?.surname}</td>
                <td className="py-2 px-3 font-mono text-xs text-gray-400">{driver?.card_number}</td>
                <td className="py-2 px-3 text-right text-gray-900">{summary.total_driving_hours}h</td>
                <td className="py-2 px-3 text-right text-gray-900">{summary.total_distance_km.toLocaleString()} km</td>
                <td className="py-2 px-3 text-right text-gray-500">{summary.days_worked}</td>
                <td className="py-2 px-3 text-right">
                  <span className={infringements.length > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                    {infringements.length}
                  </span>
                </td>
                <td className="py-2 px-3 text-center">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    complianceScore >= 90 ? 'bg-green-500' :
                    complianceScore >= 70 ? 'bg-amber-500' : 'bg-red-500'
                  }`} />
                  <span className="text-xs text-gray-500 ml-1.5">{complianceScore}%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          Currently showing single driver. Multi-driver fleet comparison available when additional drivers are loaded.
        </p>
      </div>
    </div>
  )
}
